import { useCallback, useContext, useEffect, useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { debounce } from '@zg-rentals/zdash';
import { Box } from '@zillow/constellation-10';
import {
  ConversationSearchBar_GetConversationSearchResultQuery,
  Folder,
  ReadRepliedStatus,
  RenterProgressStatus,
  useConversationSearchBar_GetConversationSearchResultLazyQuery,
} from '../../types/GraphqlTypes';
import { InboxPageMobileView } from '../../types/InboxPageMobileView';
import { MAX_OLD_CONVERSATION_DAYS } from '../../constants/ConfigConstants';
import {
  inboxPageConversationIdToFilterByVariable,
  inboxPageConversationSearchTextVariable,
  inboxPageIsConversationSearchBarFocusedVariable,
  inboxPageShowEmptyContainerVariable,
} from '../../lib/apollo';
import { InboxPageContext } from '../../contexts/InboxPageContextProvider';
import useInboxRouterHelper from '../../hooks/useInboxRouterHelper';
import getValueFromEnum from '../../utils/getValueFromEnum';
import getUtcTimestampMsFromDaysBack from '../../utils/getUtcTimestampMsFromDaysBack';
import getIsPhoneNumber from '../../utils/getIsPhoneNumber';
import getFormattedSearchText from '../../utils/getFormattedSearchText';
import SearchBar, {
  ERROR_MESSAGE,
  MAX_CHARACTERS_MESSAGE,
  MAX_LENGTH_SEARCH_TEXT,
  SearchResultItem,
  filterSpecialCharacters,
} from '../SearchBar';
import { logApolloError } from '../../utils/errorUtils';
import { logError } from '@zg-rentals/log-error';

type ConversationSearchResults =
  ConversationSearchBar_GetConversationSearchResultQuery['ZRM_getConversationSearchResult']['searchResult'];

const formatConversationSearchResults = (
  conversationSearchResults: ConversationSearchResults,
  showRenterPhoneNumber = false,
): Array<SearchResultItem> => {
  return conversationSearchResults.map((conversationSearchResult) => {
    const { conversationId, folder, listingAlias, renterName, renterPhoneNumber, streetAddress } =
      conversationSearchResult || {};
    const label = showRenterPhoneNumber ? `${renterName} ${renterPhoneNumber}` : renterName || '';

    return {
      meta: streetAddress || '',
      value: renterName || '',
      label,
      conversationId: conversationId || '',
      listingAlias: listingAlias || '',
      folder: folder || '',
    };
  });
};

export default function ConversationSearchBar() {
  const { getFiltersQueryString, updatePath, removeQueryParam } = useInboxRouterHelper();
  const { mobileView } = useContext(InboxPageContext);

  const [fetchSearchResults, { fetchMore: fetchMoreSearchResults, error }] =
    useConversationSearchBar_GetConversationSearchResultLazyQuery();
  const { data: inboxPageVariables } = useQuery(GET_INBOX_PAGE_VARIABLES, { fetchPolicy: 'cache-only' });
  const { inboxPageConversationSearchText, inboxPageIsLoading } = inboxPageVariables;

  const [searchText, setSearchText] = useState('');
  const [helperText, setHelperText] = useState('');
  const [isDisabled, setIsDisabled] = useState(true);
  const [formattedSearchResults, setFormattedSearchResults] = useState<Array<SearchResultItem>>([]);
  const [hasMoreSearchResults, setHasMoreSearchResults] = useState(false);

  const { startTimestampUtcMS, endTimestampUtcMs } = getUtcTimestampMsFromDaysBack(MAX_OLD_CONVERSATION_DAYS);

  const debounceFetchSearchResult = useCallback(
    debounce((inputText: string) => {
      const isPhoneNumber = getIsPhoneNumber(inputText);
      const formattedSearchText = getFormattedSearchText(inputText);

      return fetchSearchResults({
        variables: {
          offset: 0,
          limit: 10,
          searchText: formattedSearchText,
          startTimestampMs: startTimestampUtcMS,
          endTimestampMs: endTimestampUtcMs,
        },
        onCompleted: ({ ZRM_getConversationSearchResult }) => {
          const { hasMore, searchResult } = ZRM_getConversationSearchResult;
          inboxPageShowEmptyContainerVariable(!searchResult.length);
          inboxPageConversationSearchTextVariable(inputText);
          const formattedSearchResultData = formatConversationSearchResults(searchResult, isPhoneNumber);
          setFormattedSearchResults(formattedSearchResultData);
          setHasMoreSearchResults(hasMore);
        },
        onError: (error) => {
          setHelperText(ERROR_MESSAGE);
          inboxPageShowEmptyContainerVariable(true);

          logApolloError(error, {
            details: 'ConversationSearchBar - Error fetching conversation search result data',
            offset: 0,
            limit: 10,
            searchText: inputText,
            startTimestampMs: startTimestampUtcMS,
            endTimestampMs: endTimestampUtcMs,
          });
        },
      });
    }, 300),
    [],
  );

  useEffect(() => {
    // Persist search text (i.e. when filters are applied)
    if (inboxPageConversationSearchText) {
      setSearchText(inboxPageConversationSearchText);
    }
  }, []);

  useEffect(() => {
    // Reset search text (Search text can be reset from ConversationList's EmptyContainer)
    if (!inboxPageConversationSearchText) {
      setSearchText('');
    }

    // Disable search while inbox page is loading
    if (!inboxPageIsLoading) {
      setIsDisabled(false);
    }
  }, [inboxPageConversationSearchText, inboxPageIsLoading]);

  const handleChangeInputText = (inputText: string) => {
    let formattedInputText = filterSpecialCharacters(inputText);
    setSearchText(inputText);

    if (helperText) {
      setHelperText('');
    }

    if (inputText.length === MAX_LENGTH_SEARCH_TEXT) {
      setHelperText(MAX_CHARACTERS_MESSAGE);
    }

    if (!formattedInputText) {
      // If the user clears the search bar, clear the conversationIdToFilterBy variable
      // ConversationList component will refetch conversations without the 660 daysBack param
      removeQueryParam('daysBack');
      inboxPageConversationIdToFilterByVariable('');
      inboxPageShowEmptyContainerVariable(false);
      inboxPageConversationSearchTextVariable('');
    } else if (formattedInputText.length > 1) {
      if (formattedInputText.length > MAX_LENGTH_SEARCH_TEXT) {
        formattedInputText = formattedInputText.substring(0, MAX_LENGTH_SEARCH_TEXT);
      }

      debounceFetchSearchResult(formattedInputText);
    }
  };

  const handleSelectRenterFromSearch = (selectedRenter: SearchResultItem) => {
    const { conversationId, folder, listingAlias, value } = selectedRenter;
    const renterNameTrimmed = value.substring(0, MAX_LENGTH_SEARCH_TEXT);

    setSearchText(renterNameTrimmed);
    inboxPageConversationSearchTextVariable(renterNameTrimmed);
    inboxPageConversationIdToFilterByVariable(conversationId || '');
    inboxPageIsConversationSearchBarFocusedVariable(false);

    updatePath({
      conversationId,
      listingAlias,
      byProperty: undefined,
      filtersQueryString: getFiltersQueryString({
        byProperty: false,
        daysBack: MAX_OLD_CONVERSATION_DAYS,
        folder: getValueFromEnum(folder.toUpperCase(), Folder),
        mobileView: mobileView || InboxPageMobileView.ConversationList,
        readRepliedStatus: ReadRepliedStatus.ShowAll,
        renterProgressStatus: RenterProgressStatus.ShowAll,
      }),
    });
  };

  const handleFetchMoreSearchResults = async (searchText: string, offset: number) => {
    try {
      await fetchMoreSearchResults({
        variables: {
          searchText,
          offset,
        },
      });
    } catch (error) {
      logError({ error, context: 'ConversationSearchBar - Error fetching more search results' });
    }
  };

  const handleFocus = () => inboxPageIsConversationSearchBarFocusedVariable(true);

  const handleBlur = () => inboxPageIsConversationSearchBarFocusedVariable(false);

  return (
    <Box
      css={{
        borderBottom: '1px solid',
        borderColor: 'border.subtle',
        padding: 'default',
      }}
    >
      <SearchBar
        isDisabled={isDisabled}
        onSelectSearchItem={handleSelectRenterFromSearch}
        placeholder="Search by name, phone #"
        searchText={searchText}
        fetchMoreSearchResults={handleFetchMoreSearchResults}
        hasError={!!error}
        hasMoreSearchResults={hasMoreSearchResults || false}
        helperText={helperText}
        onChangeInputText={handleChangeInputText}
        searchResults={formattedSearchResults}
        onFocus={handleFocus}
        onBlur={handleBlur}
      />
    </Box>
  );
}

const GET_INBOX_PAGE_VARIABLES = gql`
  query {
    inboxPageConversationSearchText @client
    inboxPageIsLoading @client
  }
`;
